import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Check, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Loader, Empty } from "@/components/Bits";
import { ManageContent } from "@/components/admin/ManageContent";
import { UsersPanel } from "@/components/admin/UsersPanel";
import { useAuth } from "@/context/AuthContext";

const VIEWS = ["Queue", "Manage content", "Users"];

const Admin = () => {
  const { user, loading } = useAuth();
  const [queue, setQueue] = useState<any>(null);
  const [tab, setTab] = useState("events");
  const [view, setView] = useState("Queue");

  const load = useCallback(() => {
    api.get("/admin/queue").then((r) => setQueue(r.data)).catch(() => setQueue(false));
  }, []);

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [user, load]);

  if (loading) return <Loader />;
  if (!user || user.role !== "admin")
    return (
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-32" data-testid="admin-denied">
        <h1 className="font-bebas text-5xl tracking-wide uppercase">Admin access required</h1>
        <p className="mt-4 text-sm text-white/50">This panel is limited to platform moderators.</p>
      </div>
    );
  if (queue === null) return <Loader />;

  const review = async (kind: string, target_id: string, decision: string) => {
    try {
      await api.post("/admin/review", { kind, target_id, decision });
      toast.success(`${kind} ${decision}d`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const lists: any = {
    events: (queue.events || []).map((e: any) => ({ id: e.event_id, title: e.title, sub: `${e.city} · ${e.organizer_name}`, body: e.summary })),
    petitions: (queue.petitions || []).map((p: any) => ({ id: p.petition_id, title: p.title, sub: `Target: ${p.target_authority} · ${p.creator_name}`, body: p.summary })),
    organizers: (queue.organizers || []).map((o: any) => ({ id: o.organizer_id, title: o.org_name, sub: `${o.user_name} · ${o.user_email}`, body: o.about })),
    blogs: (queue.blogs || []).map((b: any) => ({ id: b.blog_id, title: b.title, sub: `${b.author_name} · ${b.category} · ${b.read_minutes} min`, body: b.summary })),
    spotlight: (queue.spotlights || []).map((s: any) => ({ id: s.spotlight_id, title: s.name, sub: `${s.category.replace(/_/g, " ")} · ${s.city} · ${s.owner_name}`, body: `${s.tagline} — ${s.about}` })),
    fellowship: (queue.fellowships || []).map((a: any) => ({ id: a.application_id, title: `${a.user_name}, ${a.age} — ${a.track.replace(/_/g, " ")}`, sub: `${a.city} · ${a.user_email}`, body: a.motivation })),
  };
  const kindMap: any = { events: "event", petitions: "petition", organizers: "organizer", blogs: "blog", spotlight: "spotlight", fellowship: "fellowship" };

  return (
    <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-16" data-testid="admin-page">
      <div className="flex items-center gap-3">
        <ShieldCheck className="text-pcyellow" />
        <h1 className="font-bebas text-5xl tracking-wide uppercase">Admin panel</h1>
      </div>

      <div className="mt-8 flex flex-wrap gap-2">
        {VIEWS.map((v) => (
          <button key={v} onClick={() => setView(v)} data-testid={`admin-view-${v.toLowerCase().replace(" ", "-")}`}
            className={`px-5 h-10 text-[11px] font-bold uppercase tracking-[0.2em] border transition-colors duration-200 ${
              view === v ? "bg-neon border-neon" : "border-white/15 text-white/50 hover:text-white"}`}>
            {v}
          </button>
        ))}
      </div>

      {view === "Manage content" && <div className="mt-10"><ManageContent /></div>}
      {view === "Users" && <div className="mt-10"><UsersPanel /></div>}

      {view === "Queue" && (<>
      <div className="mt-10 flex gap-2 border-b border-white/10 overflow-x-auto">
        {Object.keys(lists).map((k) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
            className={`px-5 h-11 text-[11px] font-bold uppercase tracking-[0.2em] border-b-2 transition-colors duration-200 ${tab === k ? "border-neon text-neon" : "border-transparent text-white/45 hover:text-white"}`}>
            {k} ({lists[k].length})
          </button>
        ))}
      </div>

      <div className="mt-10 grid gap-4">
        {lists[tab].length === 0 && <Empty text={`No pending ${tab}.`} testid={`admin-empty-${tab}`} />}
        {lists[tab].map((item: any, i: number) => (
          <div key={item.id} className="border border-white/10 bg-ink-surface p-6 flex flex-wrap gap-6 items-start" data-testid={`admin-item-${tab}-${i}`}>
            <div className="flex-1 min-w-[240px]">
              <h3 className="font-outfit text-lg font-bold">{item.title}</h3>
              <p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-white/35">{item.sub}</p>
              <p className="mt-3 text-sm text-white/55">{item.body}</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => review(kindMap[tab], item.id, "approve")} data-testid={`approve-${tab}-${i}`}
                className="h-10 px-5 bg-neon flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.18em] hover:pc-glow transition-shadow">
                <Check size={15} /> Approve
              </button>
              <button onClick={() => review(kindMap[tab], item.id, "reject")} data-testid={`reject-${tab}-${i}`}
                className="h-10 px-5 border border-white/20 flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.18em] hover:border-neon hover:text-neon transition-colors">
                <X size={15} /> Reject
              </button>
            </div>
          </div>
        ))}
      </div>
      </>)}
    </div>
  );
};

export default Admin;
